import { css, cx } from "@emotion/css";
import type { HTMLAttributes, CSSProperties } from "react";

/* Styles */
const stepperWrapper = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const stepper = css({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  padding: '15px 5px',
});

const step = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  '&:first-child': {
    justifyContent: 'flex-start',
  },
  '&:last-child': {
    justifyContent: 'flex-end',
  },
});

const stepBar = css({
  height: '5px',
  backgroundColor: 'rgba(var(--pcrgba-200), 0.5)',
  flex: 1,
  position: 'relative',
  margin: '0 10px',
});

const stepBarActiveHelper = css({
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: 0,
  backgroundColor: 'var(--pc-500)',
  borderRadius: '2px',
  transition: 'all 0.3s ease',
  '&.active': {
    width: '100%',
  },
});

const stepBallWrapper = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const label = css({
  position: 'absolute',
  width: 'auto',
  top: '50px',
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: 'var(--pc-700)',
  textAlign: 'center',
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const mobileLabel = css({
  display: 'none',
  textAlign: 'center',
  fontWeight: 600,
  marginBottom: 'var(--spacing-md)',
  color: 'var(--pc-700)',
  '@media (max-width: 768px)': {
    display: 'block',
    fontSize: 'var(--fs-400)',
  },
});

const stepBall = css({
  width: '42px',
  height: '42px',
  borderRadius: '50%',
  backgroundColor: 'var(--primary-color)',
  border: '2px solid rgba(var(--pcrgba-300), 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 600,
  color: 'var(--pc-600)',
  position: 'relative',
  zIndex: 2,
  transition: 'all 0.2s ease',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 920px)': {
    width: '36px',
    height: '36px',
    fontSize: 'var(--fs-350)',
  },
  '@media (max-width: 480px)': {
    width: '32px',
    height: '32px',
    fontSize: 'var(--fs-300)',
  },
});

const stepBallActiveHelper = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  transform: 'scale(0)',
  opacity: 0,
  transition: 'all 0.3s ease',
  '&.active': {
    transform: 'scale(1)',
    opacity: 0.12,
    backgroundColor: 'var(--pc-500)',
  },
});

const stepBallActive = css({
  borderColor: 'var(--pc-500)',
  color: 'var(--pc-700)',
});

/* Component Props */
export interface Step {
  label: string;
  number: number;
}

export interface StepperProps extends HTMLAttributes<HTMLDivElement> {
  steps: Step[];
  currentStep: number;
  style?: CSSProperties;
}

export function Stepper({ steps, currentStep, className, style, ...props }: StepperProps) {
  return (
    <div className={cx(stepperWrapper, className)} style={style} {...props}>
      <p className={mobileLabel}>{steps[currentStep]?.label || ''}</p>
      <div className={stepper}>
        {steps.map((s, index) => (
          <div key={s.number} style={{ display: 'contents' }}>
            <div className={step}>
              <div className={stepBallWrapper}>
                <p className={label}>{s.label}</p>
                <div className={cx(stepBall, currentStep >= index && stepBallActive)}>
                  <div
                    className={cx(
                      stepBallActiveHelper,
                      currentStep >= index && 'active'
                    )}
                  />
                  <p>{s.number}</p>
                </div>
              </div>
            </div>

            {index < steps.length - 1 && (
              <div className={stepBar}>
                <div
                  className={cx(
                    stepBarActiveHelper,
                    currentStep > index && 'active'
                  )}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Stepper;
